import type { DisciplineProgressReportsIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getDisciplineProgressReportsFilterParams } from '../lib/discipline-progress-reports-query'
import { useDisciplineProgressReportsStore } from '../model/progress-reports/store'

interface DisciplineProgressReportsApiOptions {
  disciplineId: string | number
}

/** Filters and pages change faster than the list answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortDisciplineProgressReportsApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useDisciplineProgressReportsStore().setLoading(false)
}

/** `GET /admin-api/disciplines/{discipline}/progress-reports` — the discipline's reports, newest first. */
export const useDisciplineProgressReportsApi = async (
  options: DisciplineProgressReportsApiOptions,
): Promise<DisciplineProgressReportsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineProgressReportsStore()

  const url = useAdminRoutes('admin.disciplines.api.progress-reports', pick({
    discipline_id: options.disciplineId,
    ...getDisciplineProgressReportsFilterParams(store.getFilters),
    per_page: store.getPerPage,
    page: store.getCurrentPage,
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    const indexData: DisciplineProgressReportsIndexData | null = data.data || null

    if (indexData) {
      store.setReports(indexData.progress_reports)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
